import { Dictionary } from 'lodash';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Company, Role, Team, User } from 'api/models';
import { ResourceManagementConfig } from 'api/resource-management/types';
import {
  GroupBy,
  ProjectsWithMembers,
  TeamsGroupedByCompany,
  UsersGroupedByCompany,
  UserWithProjects,
} from 'components/organisms/resource-management/types';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { GroupByProjects } from 'components/pages/resource-management/group-by-projects';
import { GroupByUsers } from 'components/pages/resource-management/group-by-users';
import { RootState } from 'data';
import { FieldsStore } from 'stores/fields';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { useUserFieldsAsFilterableFields } from 'utils/user-fields';
import { NotFound } from '../404';

interface Props {
  groupBy: GroupBy;
  roles: Role[];
  users: User[];
  teams: Team[];
  projects: ProjectsWithMembers[];
  roleLookup: Dictionary<Role>;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  projectLookup: Dictionary<ProjectsWithMembers>;
  teamMembership: Record<string, Team[]>;
  usersWithProjects: Dictionary<UserWithProjects>;
  workingDaysConfig: WorkingDaysConfiguration;
  resourceManagementConfig?: ResourceManagementConfig;
  userIdsGroupedByCompany: UsersGroupedByCompany;
  partnerCompanyLookup: Dictionary<Company>;
  teamIdsGroupedByCompany: TeamsGroupedByCompany;
}

const excludedProjectFieldsForFilter = [
  'template',
  'estimatedDueDate',
  'currentPhases',
];

export const ResourceManagementViewSwitcher: FC<Props> = ({
  groupBy,
  roles,
  users,
  teams,
  projects,
  roleLookup,
  userLookup,
  teamLookup,
  projectLookup,
  teamMembership,
  usersWithProjects,
  workingDaysConfig,
  resourceManagementConfig,
  partnerCompanyLookup,
  userIdsGroupedByCompany,
  teamIdsGroupedByCompany,
}) => {
  const { fields: userFields, isFieldsLoading: isUserFieldsLoading } =
    useUserFieldsAsFilterableFields();
  const { fields: projectFields, loading: isProjectFieldsLoading } =
    useProjectFieldsAsFilterableFields();

  const fieldsStore = useSelector(({ fields: _fields }: RootState) => _fields);
  const statusFieldId = FieldsStore.loaded(fieldsStore)
    ? FieldsStore.getProjectStatusFieldId(fieldsStore)
    : undefined;

  const projectFieldsAfterExclusion = useMemo(
    () =>
      isProjectFieldsLoading
        ? []
        : projectFields.filter(
            (x) => !excludedProjectFieldsForFilter.includes(x.fieldName),
          ),
    [projectFields, isProjectFieldsLoading],
  );

  switch (groupBy) {
    case GroupBy.Users:
      return (
        <GroupByUsers
          key={GroupBy.Users}
          projects={projects}
          users={users}
          roles={roles}
          teams={teams}
          projectLookup={projectLookup}
          userLookup={userLookup}
          roleLookup={roleLookup}
          teamLookup={teamLookup}
          teamMembership={teamMembership}
          usersWithProjects={usersWithProjects}
          workingDaysConfig={workingDaysConfig}
          userFields={userFields}
          isUserFieldsLoading={isUserFieldsLoading}
          projectFields={projectFieldsAfterExclusion}
          isProjectFieldsLoading={isProjectFieldsLoading}
          statusFieldId={statusFieldId}
          resourceManagementConfig={resourceManagementConfig}
          partnerCompanyLookup={partnerCompanyLookup}
          userIdsGroupedByCompany={userIdsGroupedByCompany}
          teamIdsGroupedByCompany={teamIdsGroupedByCompany}
        />
      );
    case GroupBy.Projects:
      return (
        <GroupByProjects
          key={GroupBy.Projects}
          projects={projects}
          users={users}
          roles={roles}
          teams={teams}
          projectLookup={projectLookup}
          userLookup={userLookup}
          roleLookup={roleLookup}
          teamLookup={teamLookup}
          teamMembership={teamMembership}
          workingDaysConfig={workingDaysConfig}
          projectFields={projectFieldsAfterExclusion}
          isProjectFieldsLoading={isProjectFieldsLoading}
          statusFieldId={statusFieldId}
          resourceManagementConfig={resourceManagementConfig}
        />
      );
    default:
      return <NotFound />;
  }
};
